import { FlowMode } from '@/enums/flow-modes.enum';

const navigate = (url: string, openInNewTab: boolean) => {
  if (openInNewTab) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }
};

/** /flow marker on every "Open workflow" from a tool: the server serves the version the tool runs. */
export const LATEST_TOOL_VERSION_PARAM = 'latestToolVersion';

const reloadInToolTab = (version?: number) => {
  const params = new URLSearchParams(window.location.search);
  // A picked version replaces the tool's one.
  params.delete(LATEST_TOOL_VERSION_PARAM);
  if (version !== undefined) {
    params.set('version', String(version));
  } else {
    params.delete('version');
  }
  params.set('view', FlowMode.App);
  window.location.href = `${window.location.pathname}?${params.toString()}`;
};

// Switch the tool's version (omit for latest). Under app-view-separation, stay on the Tool tab;
// off the flag, fall back to the prior bare-path / ?version reload.
export const goToToolVersion = (isToolViewEnabled: boolean | undefined, version?: number) => {
  if (isToolViewEnabled) {
    reloadInToolTab(version);
    return;
  }
  window.location.href =
    version !== undefined ? `${window.location.pathname}?version=${version}` : window.location.pathname;
};

// A ?version= URL value as a 1-based version number, or undefined when absent/invalid ('0', '3.5', 'abc').
export const parseVersionParam = (value: string | null): number | undefined => {
  const parsed = Number(value);
  return value && Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

export const goToRecipe = (id: string | null, openInNewTab: boolean = false, isNewFile: boolean = false) => {
  if (!id) {
    return;
  }
  navigate(`/flow/${id}${isNewFile ? '?new=true' : ''}`, openInNewTab);
};

/**
 * Opens the workflow canvas and keeps the viewer on it. With `flow-tool-redirect-requires-param` off,
 * `/flow` sends a non-owner to the tool page when they can view the tool (WEA-5088), and
 * `?view=workflow` is that redirect's opt-out (Recipe.tsx). With it on, only `?view=app` redirects.
 */
export const goToWorkflowCanvas = (id: string | null, openInNewTab: boolean = false) => {
  if (!id) {
    return;
  }
  navigate(`/flow/${id}?view=${FlowMode.Workflow}`, openInNewTab);
};

/**
 * The canvas link every "Open workflow" from a tool takes. `view=workflow` keeps a tool viewer from being sent
 * back to /tool. Always carries `latestToolVersion`: the server, not the reader's flag, decides whether it
 * applies. A forwarded `version` (a tool page opened on an older version) still wins over it.
 */
export const toolWorkflowPath = (workflowId: string, params?: URLSearchParams) => {
  const search = new URLSearchParams(params);
  search.set('view', FlowMode.Workflow);
  search.set(LATEST_TOOL_VERSION_PARAM, 'true');
  return `/flow/${workflowId}?${search.toString()}`;
};

/** Whether this /flow page was opened from a tool, on the version the tool runs. */
export const isOnToolVersionLink = () =>
  new URLSearchParams(window.location.search).get(LATEST_TOOL_VERSION_PARAM) === 'true';

/** The page's params without the tool's version: no marker and no explicit `version`. */
const paramsWithoutToolVersion = () => {
  const params = new URLSearchParams(window.location.search);
  params.delete(LATEST_TOOL_VERSION_PARAM);
  params.delete('version');
  return params;
};

/** Drops the tool-version marker from the URL without a reload, so the next fetch reads the latest version. */
export const leaveToolVersionLink = () => {
  if (!isOnToolVersionLink()) return;
  const search = paramsWithoutToolVersion().toString();
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
};

/** Leaves the tool's version for the live workflow canvas, keeping the page's other params. */
export const goToLiveDraft = () => {
  const params = paramsWithoutToolVersion();
  params.set('view', FlowMode.Workflow);
  window.location.href = `${window.location.pathname}?${params.toString()}`;
};

/**
 * The workflow's draft preview, with the legacy Tool view retained while the redesign is off.
 *
 * Not `/tool/:toolId`: that page serves the **published** tool, so it can't show the draft the
 * builder is editing — the design asks for panel edits to land on the preview immediately. It also
 * needs an id that only the first deploy mints, which left the first tool unreachable.
 */
export const workflowToolViewUrl = (
  workflowId: string,
  isToolViewRedesignEnabled = false,
) => {
  if (!isToolViewRedesignEnabled) return `/flow/${workflowId}?view=${FlowMode.App}`;
  const params = (new URLSearchParams(window.location.search)).toString();
  return `/tool/preview/${workflowId}${params ? `?${params}` : ''}`;
};

// Deliberately a hard navigation, like every other jump to /tool. Leaving the workflow canvas tears
// down ReactFlow and the WASM design core, and the router runs with `v7_startTransition`, so a soft
// route swap out of that still-mounted, still-updating tree commits the URL and defers the render —
// the address bar changes and the page never switches (WEA-6499).
export const goToTool = (toolId: string | null, openInNewTab: boolean = false, version?: string | null) => {
  if (!toolId) {
    return;
  }
  navigate(`/tool/${toolId}${version ? `?version=${version}` : ''}`, openInNewTab);
};
